/*
 * An address, as parts and as one line.
 *
 * Every address box in the app used to be a single free-text field, so what
 * landed in it was whatever the person typing felt like — one line, three
 * lines with no city, a postcode buried mid-string. A purchase order and a
 * customs form both want a city and a postcode on their own, and neither can
 * be read back out of that.
 *
 * The parts are stored in their own columns, and each table's one-line
 * `address` column is kept as the composed form of them. Everything that read
 * that column before — the order PDF, the order API, the extractor's
 * blank-supplier check, and the location matcher's `address_normalized` key —
 * keeps working without knowing the parts exist.
 *
 * This started as the supplier form's helper. Locations need exactly the same
 * rules, and two copies of an address formatter is how two screens end up
 * printing the same address two ways.
 */

export interface AddressParts {
  address_line1?: string | null;
  address_line2?: string | null;
  address_line3?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

/** The parts a form edits, as the strings its inputs hold. */
export type AddressFormParts = Required<{
  [K in keyof Omit<AddressParts, 'country'>]: string;
}>;

/** All seven parts as strings, country included — what an address input holds. */
export type AddressInputsValue = AddressFormParts & { country: string };

export const EMPTY_ADDRESS_INPUTS: AddressInputsValue = {
  address_line1: '',
  address_line2: '',
  address_line3: '',
  city: '',
  region: '',
  postal_code: '',
  country: '',
};

const PART_KEYS = [
  'address_line1',
  'address_line2',
  'address_line3',
  'city',
  'region',
  'postal_code',
] as const;

function clean(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Whether any of the address parts has actually been filled in.
 *
 * Country is deliberately excluded. It is `not null` on suppliers and set on
 * most locations, so counting it would make every record that has never had
 * an address look like it has one — which is precisely what made a legacy
 * supplier's address read as nothing but their country name.
 */
export function hasAddressParts(parts: AddressParts): boolean {
  return PART_KEYS.some((key) => clean(parts[key]).length > 0);
}

/**
 * The address as its own lines, in the order a shipping label reads them.
 *
 * City, region and postcode share a line, the way every postal format writes
 * them, and empty parts drop out rather than leaving a blank line or a stray
 * comma.
 */
export function addressLines(parts: AddressParts): string[] {
  const streetLines = [
    clean(parts.address_line1),
    clean(parts.address_line2),
    clean(parts.address_line3),
  ].filter((line) => line.length > 0);

  const locality = [clean(parts.city), clean(parts.region)]
    .filter((part) => part.length > 0)
    .join(', ');

  /*
   * The postcode sits after the region with a space, not a comma: "CA 94103",
   * "Guangdong 518101". Written with a comma it reads as a separate place.
   */
  const postalCode = clean(parts.postal_code);
  const localityLine = [locality, postalCode]
    .filter((part) => part.length > 0)
    .join(' ');

  const lines = [...streetLines, localityLine].filter(
    (line) => line.length > 0,
  );

  /*
   * The country goes on the end, and only when the address does not already
   * say it. A record saved before the parts existed has its whole hand-typed
   * address in line 1, and plenty of those already spell the country out —
   * appended blindly, saving one without touching the address wrote
   * "…, Hong Kong, Hong Kong" back to it. The order PDF makes the same check
   * for the same reason.
   */
  // Some legacy suppliers have "Unknown" as a placeholder country. It is not
  // part of their mailing address and must not replace known locality details.
  const country = clean(parts.country);
  const last = lines.at(-1)?.toLowerCase() ?? '';

  if (
    country &&
    country.toLowerCase() !== 'unknown' &&
    !last.endsWith(country.toLowerCase())
  ) {
    lines.push(country);
  }

  return lines;
}

/**
 * The one-line form written back to a table's `address` column.
 *
 * Returns null when nothing has been filled in, so an untouched address stays
 * null rather than becoming an empty string that reads as "provided".
 */
export function composeAddress(parts: AddressParts): string | null {
  /*
   * Country alone is not an address. It is required on every supplier, so
   * composing it on its own would turn "no address on file" into an address
   * reading "China" on a record where nobody has ever typed one.
   */
  if (!hasAddressParts(parts)) return null;

  const lines = addressLines(parts);

  return lines.length > 0 ? lines.join(', ') : null;
}

/**
 * The address to show for a record that may predate the structured columns.
 *
 * Rows saved before the parts existed have their whole address sitting in the
 * legacy `address` column and nothing in the parts; so do locations the
 * extractor creates off an upload, which only ever writes the one line.
 * Rather than backfill every row in a shared database, the fallback lives
 * here: parts first, legacy string when there are no parts.
 */
export function resolveAddress(
  record: AddressParts & { address?: string | null },
): string | null {
  if (!hasAddressParts(record)) {
    return clean(record.address) || null;
  }

  return composeAddress(record);
}

/**
 * Where an existing record's address goes when its edit form opens.
 *
 * A record with structured parts gets them back verbatim. One with only the
 * legacy string gets it in line 1, where it is visible and editable, instead
 * of an address block that looks empty next to a detail page that plainly
 * shows an address.
 */
export function splitAddressForForm(
  record: AddressParts & { address?: string | null },
): AddressFormParts {
  const parts: AddressFormParts = {
    address_line1: clean(record.address_line1),
    address_line2: clean(record.address_line2),
    address_line3: clean(record.address_line3),
    city: clean(record.city),
    region: clean(record.region),
    postal_code: clean(record.postal_code),
  };

  if (hasAddressParts(parts)) return parts;

  return { ...parts, address_line1: clean(record.address) };
}

/**
 * True when a record's address has never been split into parts, so the whole
 * of it is sitting in line 1 of the form.
 */
export function isUnsplitAddress(
  record: AddressParts & { address?: string | null },
): boolean {
  return !hasAddressParts(record) && clean(record.address).length > 0;
}

/** Whether any of the six address parts differs from how the form opened. */
function arePartsEdited(seeded: AddressParts, current: AddressParts): boolean {
  return PART_KEYS.some((key) => clean(seeded[key]) !== clean(current[key]));
}

/** Whether any part of the address, country included, differs from how the form opened. */
export function isAddressEdited(
  seeded: AddressParts,
  current: AddressParts,
): boolean {
  return (
    arePartsEdited(seeded, current) ||
    clean(seeded.country) !== clean(current.country)
  );
}

/**
 * Whether saving should rebuild the one-line address from the parts.
 *
 * On a record that was already structured, yes whenever anything changed,
 * country included: its `address` was composed from those parts and country in
 * the first place, so a new country belongs in it.
 *
 * On a record that has never been split, only when the six address parts
 * changed. Its `address` is the line a document printed, and the country is a
 * separate column. Filling in a blank country on a location the extractor
 * created — the one thing someone is most likely to open it for, since a US
 * invoice ending in a ZIP code yields no country at all — must not append that
 * country to the printed line. That changed the key the upload matcher looks
 * the location up by, and the next invoice from the same factory filed it a
 * second time.
 */
function shouldRecompose({
  wasUnsplit,
  seeded,
  current,
}: {
  wasUnsplit: boolean;
  seeded: AddressParts;
  current: AddressParts;
}): boolean {
  return wasUnsplit
    ? arePartsEdited(seeded, current)
    : isAddressEdited(seeded, current);
}

/**
 * The structured parts to write when a form is saved.
 *
 * A record that has never been split, saved without its address parts being
 * touched, keeps its parts empty. Writing them would copy the whole one-line
 * address into line 1 and mark the record as structured when nobody has
 * structured it — and the note asking for the city and postcode to be moved
 * out would never be shown again.
 */
export function addressPartsForSave({
  wasUnsplit,
  seeded,
  current,
}: {
  wasUnsplit: boolean;
  seeded: AddressParts;
  current: AddressParts;
}): Required<Omit<AddressParts, 'country'>> {
  if (wasUnsplit && !arePartsEdited(seeded, current)) {
    return {
      address_line1: null,
      address_line2: null,
      address_line3: null,
      city: null,
      region: null,
      postal_code: null,
    };
  }

  return {
    address_line1: clean(current.address_line1) || null,
    address_line2: clean(current.address_line2) || null,
    address_line3: clean(current.address_line3) || null,
    city: clean(current.city) || null,
    region: clean(current.region) || null,
    postal_code: clean(current.postal_code) || null,
  };
}

/**
 * The one-line address to write when a form is saved.
 *
 * When the address block was not touched, the stored string goes back exactly
 * as it was, byte for byte. This matters most for locations: the matcher that
 * stops an uploaded document filing the same warehouse twice compares against
 * a key the database derives from this column, and recomposing an untouched
 * address — appending a country the document did not print, say — changes
 * that key. A supplier's factory would then be filed a second time on its next
 * invoice, because someone opened the location to fix a phone number.
 *
 * Only an address someone actually edited is recomposed from its parts — and
 * on a never-split record, changing only the country is not an edit of the
 * printed line (see `shouldRecompose`).
 */
export function composeAddressForSave({
  stored,
  wasUnsplit,
  seeded,
  current,
}: {
  /** What the record's `address` column held when the form opened. */
  stored: string | null | undefined;
  /** The record had only the one-line address when the form opened. */
  wasUnsplit: boolean;
  /** The parts and country the form opened with. */
  seeded: AddressParts;
  /** The parts and country the form is submitting. */
  current: AddressParts;
}): string | null {
  if (
    !shouldRecompose({ wasUnsplit, seeded, current }) &&
    typeof stored === 'string' &&
    stored.trim()
  ) {
    return stored;
  }

  return composeAddress(current);
}

const POSTAL = /^(?=[A-Z0-9 -]*\d)[A-Z0-9][A-Z0-9 -]{2,9}$/i;
const REGION_AND_POSTAL =
  /^([^\d,]{2,40}?)\s+((?=[A-Z0-9 -]*\d)[A-Z0-9][A-Z0-9 -]{2,9})$/i;

/**
 * Splits a one-line legacy address ("12 Gate Rd, Dongguan, Guangdong 523000,
 * China") into street, city, region and postal code.
 *
 * Only splits when the shape is clear: a postal code at the end (alone or
 * after the region) and a city with no digits before it. Anything else stays
 * whole in line 1, so nothing is guessed wrong.
 */
export function parseOneLineAddress(
  address: string | null | undefined,
  country?: string | null,
): AddressFormParts {
  const whole = clean(address);
  const empty: AddressFormParts = {
    address_line1: whole,
    address_line2: '',
    address_line3: '',
    city: '',
    region: '',
    postal_code: '',
  };
  const tokens = whole
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter(Boolean);
  const countryName = clean(country).toLowerCase();
  if (
    tokens.length > 1 &&
    countryName &&
    tokens.at(-1)!.toLowerCase() === countryName
  ) {
    tokens.pop();
  }
  if (tokens.length < 3) return empty;

  let region = '';
  let postal = '';
  const last = tokens.pop()!;
  const combined = last.match(REGION_AND_POSTAL);
  if (POSTAL.test(last) && /^\S+$|^\d[\d -]*$/.test(last)) {
    postal = last;
    const maybeRegion = tokens.at(-1) ?? '';
    if (tokens.length >= 3 && maybeRegion && !/\d/.test(maybeRegion)) {
      region = tokens.pop()!;
    }
  } else if (combined) {
    region = combined[1]!.trim();
    postal = combined[2]!.trim();
  } else {
    return empty;
  }

  const city = tokens.pop() ?? '';
  if (!city || /\d/.test(city) || tokens.length === 0) return empty;

  return {
    address_line1: tokens.join(', '),
    address_line2: '',
    address_line3: '',
    city,
    region,
    postal_code: postal,
  };
}
